'use client';
import React, { useState } from 'react';
import { X, Phone, CheckCircle, Lock, Copy, Check, Bitcoin, AlertCircle, Loader2 } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { toast } from 'sonner';
import type { Property } from './mockData';
import { formatPrice } from './mockData';
import AppImage from '@/components/ui/AppImage';

interface Props {
  property: Property;
  onClose: () => void;
}

type Step = 'info' | 'payment' | 'crypto' | 'revealed';
type PaymentMethod = 'paypal' | 'usdt';

// Mock revealed contact info
const MOCK_PHONE = '+966 50 123 4567';
const MOCK_WHATSAPP = 'wa.me/966501234567';

// Site owner USDT BEP20 (BSC) wallet — update via env var NEXT_PUBLIC_USDT_WALLET_ADDRESS
const USDT_WALLET = process.env.NEXT_PUBLIC_USDT_WALLET_ADDRESS || '0x9C9bFB619eAdC54b5a3dD707fA5Af93D06cff6f2';
const REQUIRED_USDT = '10.00';

export default function ContactUnlockModal({ property, onClose }: Props) {
  const [step, setStep] = useState<Step>('info');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('paypal');
  const [copied, setCopied] = useState(false);
  const [walletCopied, setWalletCopied] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  const handleCopy = () => {
    navigator.clipboard.writeText(MOCK_PHONE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(USDT_WALLET);
    setWalletCopied(true);
    setTimeout(() => setWalletCopied(false), 2000);
  };

  const handleVerifyTx = async () => {
    if (!txHash.trim()) {
      setVerifyError('يرجى إدخال رقم التحويل (Transaction Hash)');
      return;
    }
    setVerifying(true);
    setVerifyError('');
    try {
      const res = await fetch('/api/crypto/verify-tx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ txHash: txHash.trim(), propertyId: String(property.id) }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStep('revealed');
        toast.success('تم التحقق من المعاملة! تم كشف بيانات التواصل');
      } else {
        setVerifyError(data.error || 'فشل التحقق من المعاملة');
      }
    } catch {
      setVerifyError('خطأ في الاتصال بالخادم، يرجى المحاولة مجدداً');
    } finally {
      setVerifying(false);
    }
  };

  const paypalClientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID || 'sb';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-modal w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {step === 'revealed' ? (
              <CheckCircle size={18} className="text-emerald-500" />
            ) : (
              <Lock size={18} className="text-primary-500" />
            )}
            <span className="font-600 text-gray-900 text-sm">
              {step === 'info' && 'كشف بيانات التواصل'}
              {step === 'payment' && 'الدفع عبر PayPal'}
              {step === 'crypto' && 'الدفع بـ USDT'}
              {step === 'revealed' && 'بيانات التواصل'}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Property Preview */}
        <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100">
          <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
            <AppImage
              src={property.image}
              alt={property.imageAlt}
              fill
              className="object-cover"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-600 text-gray-900 truncate">{property.title}</p>
            <p className="text-xs text-gray-500">{property.city} — {property.district}</p>
            <p className="text-xs font-700 text-primary-600">{formatPrice(property.price, property.priceUnit)}</p>
          </div>
          <div className="text-left">
            <span className="text-xs text-gray-500 block">المالك</span>
            <span className="text-xs font-600 text-gray-800">{property.ownerName}</span>
          </div>
        </div>

        {/* Step: Info */}
        {step === 'info' && (
          <div className="p-5">
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Phone size={24} className="text-primary-500" />
              </div>
              <h3 className="font-700 text-gray-900 mb-1">اكشف رقم الهاتف الآن</h3>
              <p className="text-sm text-gray-500">تواصل مباشرة مع المالك بعد دفع رسوم رمزية</p>
            </div>

            <div className="bg-primary-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">رسوم كشف بيانات التواصل</span>
                <span className="text-lg font-800 text-primary-600">$10.00 USD</span>
              </div>
              <div className="text-xs text-gray-500">رسوم لمرة واحدة — يشمل رقم الهاتف والواتساب</div>
            </div>

            <ul className="space-y-2 mb-5">
              {[
                'رقم الهاتف المباشر للمالك',
                'رابط واتساب للتواصل الفوري',
                'صالح لمدة 7 أيام من تاريخ الكشف',
              ].map((item, i) => (
                <li key={`feature-${i}`} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            {/* Payment method selector */}
            <p className="text-xs text-gray-500 mb-2 font-500">اختر طريقة الدفع:</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setPaymentMethod('paypal')}
                className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all ${
                  paymentMethod === 'paypal' ?'border-primary-500 bg-primary-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <svg viewBox="0 0 101 32" className="h-5" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#003087" d="M12.237 2.8H6.437c-.4 0-.7.3-.8.7L3.237 18.6c0 .3.2.5.5.5h2.8c.4 0 .7-.3.8-.7l.6-3.8c.1-.4.4-.7.8-.7h1.9c3.9 0 6.2-1.9 6.8-5.6.3-1.6 0-2.9-.7-3.8-.9-.9-2.3-1.7-4.5-1.7zm.7 5.5c-.3 2.1-1.9 2.1-3.5 2.1h-.9l.6-3.9c0-.2.2-.4.4-.4h.4c1.1 0 2.1 0 2.6.6.3.4.5 1 .4 1.6z"/>
                  <path fill="#003087" d="M35.437 8.2h-2.8c-.2 0-.4.2-.4.4l-.1.8-.2-.3c-.6-.9-2-1.2-3.4-1.2-3.2 0-5.9 2.4-6.4 5.8-.3 1.7.1 3.3 1 4.4.9 1 2.1 1.4 3.6 1.4 2.6 0 4-.7 4-.7l-.1.8c0 .3.2.5.5.5h2.5c.4 0 .7-.3.8-.7l1.5-9.8c0-.3-.2-.4-.5-.4zm-3.9 5.6c-.3 1.6-1.5 2.7-3.2 2.7-.8 0-1.5-.3-1.9-.8-.4-.5-.5-1.2-.4-2 .3-1.6 1.6-2.7 3.2-2.7.8 0 1.5.3 1.9.8.4.6.5 1.3.4 2z"/>
                  <path fill="#003087" d="M49.737 8.2h-2.8c-.3 0-.5.1-.6.4l-3.7 5.4-1.6-5.2c-.1-.3-.4-.6-.8-.6h-2.8c-.3 0-.5.3-.4.6l2.9 8.6-2.7 3.9c-.2.3 0 .7.3.7h2.8c.3 0 .5-.1.6-.4l8.8-12.7c.2-.3 0-.7-.3-.7z"/>
                  <path fill="#009cde" d="M58.637 2.8h-5.8c-.4 0-.7.3-.8.7l-2.4 15.1c0 .3.2.5.5.5h3c.3 0 .5-.2.5-.5l.7-4.2c.1-.4.4-.7.8-.7h1.9c3.9 0 6.2-1.9 6.8-5.6.3-1.6 0-2.9-.7-3.8-.9-.9-2.3-1.5-4.5-1.5zm.7 5.5c-.3 2.1-1.9 2.1-3.5 2.1h-.9l.6-3.9c0-.2.2-.4.4-.4h.4c1.1 0 2.1 0 2.6.6.3.4.5 1 .4 1.6z"/>
                  <path fill="#009cde" d="M81.837 8.2h-2.8c-.2 0-.4.2-.4.4l-.1.8-.2-.3c-.6-.9-2-1.2-3.4-1.2-3.2 0-5.9 2.4-6.4 5.8-.3 1.7.1 3.3 1 4.4.9 1 2.1 1.4 3.6 1.4 2.6 0 4-.7 4-.7l-.1.8c0 .3.2.5.5.5h2.5c.4 0 .7-.3.8-.7l1.5-9.8c0-.3-.2-.4-.5-.4zm-3.9 5.6c-.3 1.6-1.5 2.7-3.2 2.7-.8 0-1.5-.3-1.9-.8-.4-.5-.5-1.2-.4-2 .3-1.6 1.6-2.7 3.2-2.7.8 0 1.5.3 1.9.8.4.6.5 1.3.4 2z"/>
                  <path fill="#009cde" d="M86.337 3.1l-2.4 15.4c0 .3.2.5.5.5h2.4c.4 0 .7-.3.8-.7l2.4-15.1c0-.3-.2-.5-.5-.5h-2.7c-.3 0-.5.2-.5.4z"/>
                </svg>
                <span className="text-xs font-600 text-gray-700">PayPal</span>
              </button>
              <button
                onClick={() => setPaymentMethod('usdt')}
                className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all ${
                  paymentMethod === 'usdt' ?'border-emerald-500 bg-emerald-50' :'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-1">
                  <Bitcoin size={16} className="text-emerald-600" />
                  <span className="text-sm font-800 text-emerald-700">USDT</span>
                </div>
                <span className="text-xs font-600 text-gray-700">كريبتو BEP20</span>
              </button>
            </div>

            <button
              onClick={() => setStep(paymentMethod === 'usdt' ? 'crypto' : 'payment')}
              className={`w-full py-3 font-600 rounded-xl transition-all duration-150 active:scale-95 text-white ${
                paymentMethod === 'usdt' ?'bg-emerald-500 hover:bg-emerald-600' :'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {paymentMethod === 'usdt' ? 'الدفع بـ USDT — 10 USDT' : 'المتابعة للدفع — $10.00'}
            </button>
            <button onClick={onClose} className="w-full py-2 mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              إلغاء
            </button>
          </div>
        )}

        {/* Step: PayPal Payment */}
        {step === 'payment' && (
          <div className="p-5">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600 mb-1">ادفع بأمان عبر</p>
              <div className="flex items-center justify-center gap-2">
                <svg viewBox="0 0 101 32" className="h-6" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#003087" d="M12.237 2.8H6.437c-.4 0-.7.3-.8.7L3.237 18.6c0 .3.2.5.5.5h2.8c.4 0 .7-.3.8-.7l.6-3.8c.1-.4.4-.7.8-.7h1.9c3.9 0 6.2-1.9 6.8-5.6.3-1.6 0-2.9-.7-3.8-.9-.9-2.3-1.7-4.5-1.7zm.7 5.5c-.3 2.1-1.9 2.1-3.5 2.1h-.9l.6-3.9c0-.2.2-.4.4-.4h.4c1.1 0 2.1 0 2.6.6.3.4.5 1 .4 1.6z"/>
                  <path fill="#003087" d="M35.437 8.2h-2.8c-.2 0-.4.2-.4.4l-.1.8-.2-.3c-.6-.9-2-1.2-3.4-1.2-3.2 0-5.9 2.4-6.4 5.8-.3 1.7.1 3.3 1 4.4.9 1 2.1 1.4 3.6 1.4 2.6 0 4-.7 4-.7l-.1.8c0 .3.2.5.5.5h2.5c.4 0 .7-.3.8-.7l1.5-9.8c0-.3-.2-.4-.5-.4zm-3.9 5.6c-.3 1.6-1.5 2.7-3.2 2.7-.8 0-1.5-.3-1.9-.8-.4-.5-.5-1.2-.4-2 .3-1.6 1.6-2.7 3.2-2.7.8 0 1.5.3 1.9.8.4.6.5 1.3.4 2z"/>
                  <path fill="#003087" d="M49.737 8.2h-2.8c-.3 0-.5.1-.6.4l-3.7 5.4-1.6-5.2c-.1-.3-.4-.6-.8-.6h-2.8c-.3 0-.5.3-.4.6l2.9 8.6-2.7 3.9c-.2.3 0 .7.3.7h2.8c.3 0 .5-.1.6-.4l8.8-12.7c.2-.3 0-.7-.3-.7z"/>
                  <path fill="#009cde" d="M58.637 2.8h-5.8c-.4 0-.7.3-.8.7l-2.4 15.1c0 .3.2.5.5.5h3c.3 0 .5-.2.5-.5l.7-4.2c.1-.4.4-.7.8-.7h1.9c3.9 0 6.2-1.9 6.8-5.6.3-1.6 0-2.9-.7-3.8-.9-.9-2.3-1.5-4.5-1.5zm.7 5.5c-.3 2.1-1.9 2.1-3.5 2.1h-.9l.6-3.9c0-.2.2-.4.4-.4h.4c1.1 0 2.1 0 2.6.6.3.4.5 1 .4 1.6z"/>
                  <path fill="#009cde" d="M81.837 8.2h-2.8c-.2 0-.4.2-.4.4l-.1.8-.2-.3c-.6-.9-2-1.2-3.4-1.2-3.2 0-5.9 2.4-6.4 5.8-.3 1.7.1 3.3 1 4.4.9 1 2.1 1.4 3.6 1.4 2.6 0 4-.7 4-.7l-.1.8c0 .3.2.5.5.5h2.5c.4 0 .7-.3.8-.7l1.5-9.8c0-.3-.2-.4-.5-.4zm-3.9 5.6c-.3 1.6-1.5 2.7-3.2 2.7-.8 0-1.5-.3-1.9-.8-.4-.5-.5-1.2-.4-2 .3-1.6 1.6-2.7 3.2-2.7.8 0 1.5.3 1.9.8.4.6.5 1.3.4 2z"/>
                  <path fill="#009cde" d="M86.337 3.1l-2.4 15.4c0 .3.2.5.5.5h2.4c.4 0 .7-.3.8-.7l2.4-15.1c0-.3-.2-.5-.5-.5h-2.7c-.3 0-.5.2-.5.4z"/>
                </svg>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-4 flex items-center justify-between">
              <span className="text-sm text-gray-600">المبلغ الإجمالي</span>
              <span className="font-800 text-primary-600 tabular-nums">$10.00 USD</span>
            </div>

            <PayPalScriptProvider
              options={{
                clientId: paypalClientId,
                currency: 'USD',
                intent: 'capture',
              }}
            >
              <PayPalButtons
                style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                createOrder={async () => {
                  const res = await fetch('/api/paypal/create-order', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ amount: '10.00', description: 'كشف بيانات التواصل مع المالك' }),
                  });
                  const data = await res.json();
                  if (!res.ok || !data.id) {
                    toast.error('فشل إنشاء طلب الدفع، يرجى المحاولة مجدداً');
                    throw new Error(data.error || 'create-order failed');
                  }
                  return data.id;
                }}
                onApprove={async (data) => {
                  const res = await fetch('/api/paypal/capture-order', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ orderID: data.orderID }),
                  });
                  const captureData = await res.json();
                  if (!res.ok) {
                    toast.error('فشل تأكيد الدفع، يرجى التواصل مع الدعم');
                    return;
                  }
                  if (captureData?.status === 'COMPLETED') {
                    setStep('revealed');
                    toast.success('تم الدفع بنجاح! تم كشف بيانات التواصل');
                  } else {
                    toast.error('لم يكتمل الدفع، يرجى المحاولة مجدداً');
                  }
                }}
                onError={() => toast.error('حدث خطأ أثناء الدفع، يرجى المحاولة مجدداً')}
                onCancel={() => toast('تم إلغاء عملية الدفع')}
              />
            </PayPalScriptProvider>

            <button onClick={() => setStep('info')} className="w-full py-2 mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              رجوع
            </button>
          </div>
        )}

        {/* Step: USDT Crypto Payment */}
        {step === 'crypto' && (
          <div className="p-5">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-2">
                <Bitcoin size={22} className="text-emerald-600" />
              </div>
              <h3 className="font-700 text-gray-900 text-sm mb-0.5">الدفع بـ USDT (BEP20 — BSC)</h3>
              <p className="text-xs text-gray-500">أرسل المبلغ إلى المحفظة أدناه ثم أدخل رقم التحويل</p>
            </div>

            {/* Amount */}
            <div className="bg-emerald-50 rounded-xl p-3 mb-3 flex items-center justify-between">
              <span className="text-sm text-gray-600">المبلغ المطلوب</span>
              <span className="font-800 text-emerald-700 tabular-nums">10.00 USDT</span>
            </div>

            {/* Wallet address */}
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1.5 font-500">عنوان المحفظة (BEP20 — Binance Smart Chain):</p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                <span className="flex-1 text-xs font-mono text-gray-800 break-all leading-relaxed">
                  {USDT_WALLET}
                </span>
                <button
                  onClick={handleCopyWallet}
                  className="flex-shrink-0 p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                  title="نسخ العنوان"
                >
                  {walletCopied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} className="text-gray-600" />}
                </button>
              </div>
            </div>

            {/* Warning */}
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 leading-relaxed">
                تأكد من إرسال USDT على شبكة <strong>BEP20 (Binance Smart Chain)</strong> فقط. إرسال على شبكة أخرى سيؤدي لفقدان الأموال.
              </p>
            </div>

            {/* TX Hash input */}
            <div className="mb-3">
              <label className="text-xs text-gray-600 font-500 block mb-1.5">
                رقم التحويل (Transaction Hash / TxID):
              </label>
              <input
                type="text"
                value={txHash}
                onChange={(e) => { setTxHash(e.target.value); setVerifyError(''); }}
                placeholder="الصق رقم التحويل هنا..."
                className="w-full px-3 py-2.5 text-xs font-mono border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white placeholder-gray-400"
                dir="ltr"
              />
              {verifyError && (
                <div className="flex items-center gap-1.5 mt-2">
                  <AlertCircle size={12} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-600">{verifyError}</p>
                </div>
              )}
            </div>

            <button
              onClick={handleVerifyTx}
              disabled={verifying || !txHash.trim()}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-600 rounded-xl transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
            >
              {verifying ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  جاري التحقق...
                </>
              ) : (
                'تحقق وكشف بيانات التواصل'
              )}
            </button>

            <button onClick={() => setStep('info')} className="w-full py-2 mt-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              رجوع
            </button>
          </div>
        )}

        {/* Step: Revealed */}
        {step === 'revealed' && (
          <div className="p-5">
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={24} className="text-emerald-500" />
              </div>
              <h3 className="font-700 text-gray-900 mb-1">تم كشف بيانات التواصل!</h3>
              <p className="text-xs text-gray-400">صالحة لمدة 7 أيام</p>
            </div>

            <div className="space-y-3 mb-5">
              <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                <div>
                  <span className="text-xs text-gray-500 block mb-0.5">رقم الهاتف</span>
                  <span className="font-700 text-gray-900 tabular-nums text-sm">{MOCK_PHONE}</span>
                </div>
                <div className="flex gap-2">
                  <a
                    href={`tel:${MOCK_PHONE.replace(/\s/g, '')}`}
                    className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    title="اتصل الآن"
                  >
                    <Phone size={15} />
                  </a>
                  <button
                    onClick={handleCopy}
                    className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    title="نسخ الرقم"
                  >
                    {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between bg-emerald-50 rounded-xl px-4 py-3 border border-emerald-100">
                <div>
                  <span className="text-xs text-emerald-600 block mb-0.5">واتساب</span>
                  <span className="font-600 text-gray-800 text-sm">تواصل عبر واتساب</span>
                </div>
                <a
                  href={`https://${MOCK_WHATSAPP}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-emerald-500 text-white text-xs font-600 rounded-lg hover:bg-emerald-600 transition-colors"
                >
                  فتح
                </a>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-600 rounded-xl transition-colors text-sm"
            >
              إغلاق
            </button>
          </div>
        )}
      </div>
    </div>
  );
}